import React, { useState, useMemo, useEffect, useRef } from "react";
import { Search, ShieldCheck, Loader2, ChevronLeft, ChevronRight, Radio } from "lucide-react";
import { toast } from "sonner";
import { checkCustomerData } from "../../api/customer";
import type { LeakResult } from "../../api/customer";

// ── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  } catch {
    return "";
  }
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
}

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

// ── Page Component ────────────────────────────────────────────────────────────

export default function CustomerCheckPage() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<LeakResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = 0;
    let height = 0;

    const mouse = { x: -1000, y: -1000, active: false };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };

    const handleMouseLeave = () => {
      mouse.active = false;
      mouse.x = -1000;
      mouse.y = -1000;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    interface Particle {
      baseX: number;
      baseY: number;
      x: number;
      y: number;
      radius: number;
      alpha: number;
    }

    let particles: Particle[] = [];

    const initParticles = () => {
      particles = [];
      if (width === 0 || height === 0) return;
      const cols = Math.floor(width / 40);
      const rows = Math.floor(height / 40);
      if (cols <= 1 || rows <= 1) return;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const bx = (c / (cols - 1)) * width;
          const by = (r / (rows - 1)) * height;

          const edgeFade = 1 - Math.abs(c - cols / 2) / (cols / 1.8);
          const topFade = r / rows;
          const alpha = Math.max(0, 0.05 + topFade * 0.18) * Math.max(0, edgeFade);

          particles.push({
            baseX: bx,
            baseY: by,
            x: bx,
            y: by,
            radius: 0.8 + (r / rows) * 1.4,
            alpha: alpha,
          });
        }
      }
    };

    let time = 0;
    const render = () => {
      const rect = canvas.getBoundingClientRect();
      if (canvas.width !== rect.width || canvas.height !== rect.height) {
        width = canvas.width = rect.width;
        height = canvas.height = rect.height;
        initParticles();
      }

      ctx.clearRect(0, 0, width, height);
      time += 0.02;

      particles.forEach((p) => {
        const idleWave = Math.sin(time + p.baseX * 0.005 + p.baseY * 0.005) * 6;
        let targetX = p.baseX;
        let targetY = p.baseY + idleWave;

        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxDist = 140;

          if (dist < maxDist) {
            const force = (maxDist - dist) / maxDist;
            const angle = Math.atan2(dy, dx);
            targetX = p.baseX - Math.cos(angle) * force * 45;
            targetY = p.baseY + idleWave - Math.sin(angle) * force * 45;
          }
        }

        p.x += (targetX - p.x) * 0.08;
        p.y += (targetY - p.y) * 0.08;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(129, 140, 248, ${p.alpha})`;
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(results.length / rowsPerPage)),
    [results.length, rowsPerPage]
  );

  const pagedResults = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return results.slice(start, start + rowsPerPage);
  }, [results, page, rowsPerPage]);

  const startIdx = (page - 1) * rowsPerPage + 1;
  const endIdx = Math.min(page * rowsPerPage, results.length);

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error("Please enter a search term");
      return;
    }
    setLoading(true);
    setPage(1);
    try {
      const data = await checkCustomerData(query);
      setResults(data);
      setHasSearched(true);
      if (data.length === 0) {
        toast.info("No threats found related to this data.");
      } else {
        toast.warning(`Found ${data.length} potential threats!`);
      }
    } catch (err) {
      console.error("Search failed", err);
      toast.error("Failed to perform search");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div ref={containerRef} className="cdc-page">
      {/* ── Background glow ─────────────────────────────────────────── */}
      <div className="cdc-bg" aria-hidden="true" />

      {/* Interactive canvas background */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* ── Hero Section ────────────────────────────────────────────── */}
      <div className="cdc-hero">
        <div className="cdc-hero-badge">
          <Radio size={12} />
          Breach Intelligence
        </div>
        <h1 className="cdc-hero-title">
          Customer Data <span className="cdc-hero-accent">Check</span>
        </h1>
        <p className="cdc-hero-sub">
          Search for exposed customer data across known breach databases
          and dark web forums
        </p>
      </div>

      {/* ── Search Card ─────────────────────────────────────────────── */}
      <div className="cdc-search-card">
        <div className="cdc-search-row">
          <div className="cdc-search-inner">
            {loading ? (
              <Loader2 className="cdc-search-icon cdc-search-icon-spin" size={18} />
            ) : (
              <Search className="cdc-search-icon" size={18} />
            )}
            <input
              id="cdc-search-input"
              type="text"
              placeholder="Enter email, domain, phone, or name..."
              className="cdc-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <button
            id="cdc-scan-btn"
            className="cdc-scan-btn"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="cdc-btn-spin" size={15} />
                Scanning…
              </>
            ) : (
              <>
                <Search size={15} />
                Scan
              </>
            )}
          </button>
        </div>
        <p className="cdc-search-hint">
          Examples: <span className="cdc-hint-example">john@company.com</span>
          {" • "}
          <span className="cdc-hint-example">acme.com</span>
          {" • "}
          <span className="cdc-hint-example">+1-555-000-1234</span>
        </p>
      </div>

      {/* ── Results Section ──────────────────────────────────────────── */}
      {hasSearched && (
        <div className="cdc-results-section">

          {/* Results header */}
          <div className="cdc-results-header">
            <span className="cdc-results-label">Search Results</span>
            {results.length > 0 ? (
              <span className="cdc-match-badge cdc-match-danger">
                {results.length} matches found
              </span>
            ) : (
              <span className="cdc-match-badge cdc-match-safe">
                No matches
              </span>
            )}
          </div>
          <div className="cdc-results-divider" />

          {/* Table or empty state */}
          {results.length > 0 ? (
            <>
              <div className="cdc-table-card">
                <div className="cdc-table-scroll">
                  <table className="cdc-table">
                    <thead>
                      <tr className="cdc-thead-row">
                        <th className="cdc-th cdc-th-num">#</th>
                        <th className="cdc-th cdc-th-title">Title / Context</th>
                        <th className="cdc-th cdc-th-date">Date Detected</th>
                        <th className="cdc-th cdc-th-action">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedResults.map((item, idx) => {
                        const rowNum = startIdx + idx;
                        const isUntitled =
                          !item.title ||
                          item.title === "Unknown Title" ||
                          item.title.trim() === "";
                        return (
                          <tr key={item.id} className="cdc-row">
                            {/* Row number */}
                            <td className="cdc-td cdc-td-num">{rowNum}</td>

                            {/* Title + snippet */}
                            <td className="cdc-td cdc-td-title">
                              <div
                                className={`cdc-row-title${isUntitled ? " cdc-row-title-untitled" : ""}`}
                              >
                                {isUntitled ? "Untitled Thread" : item.title}
                              </div>
                              {item.snippet && (
                                <div className="cdc-row-snippet">
                                  {item.snippet}
                                </div>
                              )}
                            </td>

                            {/* Date */}
                            <td className="cdc-td cdc-td-date">
                              {item.date ? (
                                <>
                                  <span className="cdc-date-main">
                                    {formatDate(item.date)}
                                  </span>
                                  <span className="cdc-date-rel">
                                    {relativeTime(item.date)}
                                  </span>
                                </>
                              ) : (
                                <span className="cdc-date-na">N/A</span>
                              )}
                            </td>

                            {/* Action */}
                            <td className="cdc-td cdc-td-action">
                              <a
                                href={`/leaks/${item.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cdc-view-btn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                View Details
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination footer */}
              <div className="cdc-pagination">
                <div className="cdc-pager-info">
                  Showing{" "}
                  <span className="cdc-pager-accent">{startIdx}</span>
                  {" – "}
                  <span className="cdc-pager-accent">{endIdx}</span>
                  {" of "}
                  <span className="cdc-pager-accent">{results.length}</span>{" "}
                  results
                </div>

                <div className="cdc-pager-controls">
                  <div className="cdc-rows-wrap">
                    <span className="cdc-rows-label">Rows:</span>
                    <select
                      className="cdc-rows-select"
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      {ROWS_PER_PAGE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    className="cdc-page-btn"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={15} />
                    Prev
                  </button>
                  <span className="cdc-page-indicator">
                    {page} / {totalPages}
                  </span>
                  <button
                    className="cdc-page-btn"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label="Next page"
                  >
                    Next
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* ── Zero / Empty State ─────────────────────────────────── */
            <div className="cdc-empty">
              <div className="cdc-empty-icon-wrap">
                <ShieldCheck size={36} className="cdc-empty-icon" />
              </div>
              <h3 className="cdc-empty-title">No Matches Found</h3>
              <p className="cdc-empty-sub">
                No results found for{" "}
                <span className="cdc-empty-query">&ldquo;{query}&rdquo;</span>.
                <br />
                This may indicate the data has not been detected in known breach
                sources.
              </p>
              <button
                className="cdc-empty-retry"
                onClick={() => {
                  setQuery("");
                  setHasSearched(false);
                  document.getElementById("cdc-search-input")?.focus();
                }}
              >
                Try a different search
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
